import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Modal from "../../components/common/Modal";
import Table from "../../components/common/Table";
import ActionButton from "../../components/common/ActionButton";
import ActionCheckbox from "../../components/common/ActionCheckbox";
import {
  useGetBusinessQuery,
  useGetBusinessStoresQuery,
  useUpdateBusinessMutation,
  useUpdateBusinessStoreStatusMutation,
  useUploadBusinessLogoMutation,
  useDeleteBusinessLogoMutation,
} from "../../store/api";
import { formatDateTime } from "../../utils/reporting";

const getBusinessCurrencyCode = (business) => {
  if (!business) return "";

  const candidates = [
    business.currencyCode,
    business.currency,
    business.currency?.code,
    business.currency?.currencyCode,
    business.defaultCurrencyCode,
    business.defaultCurrency?.code,
    business.defaultCurrency?.currencyCode,
  ];

  const resolvedCurrencyCode = candidates.find(
    (value) => typeof value === "string" && value.trim().length > 0,
  );

  return resolvedCurrencyCode?.trim() || "";
};

const getStoreCurrencyCode = (store, business) => {
  if (!store && !business) return "";

  const candidates = [
    store?.currencyCode,
    store?.currency,
    store?.currency?.code,
    store?.currency?.currencyCode,
    store?.defaultCurrencyCode,
    store?.defaultCurrency?.code,
    store?.defaultCurrency?.currencyCode,
    getBusinessCurrencyCode(business),
  ];

  const resolvedCurrencyCode = candidates.find(
    (value) => typeof value === "string" && value.trim().length > 0,
  );

  return resolvedCurrencyCode?.trim() || "";
};

const BusinessDetail = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const {
    data: business,
    isLoading,
    isError,
    error,
  } = useGetBusinessQuery(businessId);
  const {
    data: storesData,
    isLoading: isStoresLoading,
    isError: isStoresError,
    error: storesError,
  } = useGetBusinessStoresQuery(businessId);
  const [updateBusiness, updateState] = useUpdateBusinessMutation();
  const [updateStoreStatus, updateStoreStatusState] =
    useUpdateBusinessStoreStatusMutation();
  const [uploadLogo, uploadState] = useUploadBusinessLogoMutation();
  const [deleteLogo, deleteLogoState] = useDeleteBusinessLogoMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [storeStatusError, setStoreStatusError] = useState("");

  const [form, setForm] = useState({
    businessName: "",
    legalName: "",
    logoUrl: "",
    status: "ACTIVE",
    countryCode: "",
    timeZone: "",
    currencyCode: "",
    contactEmail: "",
    contactPhone: "",
  });

  React.useEffect(() => {
    if (business) {
      setForm({
        businessName: business.businessName || "",
        legalName: business.legalName || "",
        logoUrl: business.logoUrl || "",
        status: business.status || "ACTIVE",
        countryCode: business.countryCode || "",
        timeZone: business.timeZone || "",
        currencyCode: getBusinessCurrencyCode(business),
        contactEmail: business.contactEmail || "",
        contactPhone: business.contactPhone || "",
      });
    }
  }, [business]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file
      if (!file.type.match(/image\/(png|jpg|jpeg)/)) {
        alert("Only PNG and JPG images are allowed");
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        alert("File size must be less than 2MB");
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;
    try {
      await uploadLogo({ businessId, file: logoFile }).unwrap();
      setLogoFile(null);
      setLogoPreview(null);
    } catch (err) {
      // error handled by UI
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await deleteLogo(businessId).unwrap();
      setShowDeleteModal(false);
    } catch (err) {
      // error handled by UI
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateBusiness({ businessId, payload: form }).unwrap();
      setIsEditing(false);
    } catch (err) {
      // error handled by UI
    }
  };

  const stores = React.useMemo(() => {
    if (Array.isArray(storesData)) return storesData;
    if (Array.isArray(storesData?.stores)) return storesData.stores;
    if (Array.isArray(storesData?.content)) return storesData.content;
    return [];
  }, [storesData]);

  const getStoreId = (store) => store.storeId || store.id || "";
  const getStoreName = (store) =>
    store.storeName || store.name || store.storeSlug || "Store";
  const getStoreCity = (store) =>
    store.city ||
    store.address?.city ||
    store.addressCity ||
    store.location?.city ||
    "-";
  const getStoreStatus = (store) => {
    if (typeof store?.status === "string" && store.status.length > 0) {
      return store.status.toUpperCase();
    }
    if (store?.isActive === true) return "ACTIVE";
    if (store?.isActive === false) return "INACTIVE";
    return "INACTIVE";
  };
  const getStoreCreatedAt = (store) => store.createdAt || store.createdOn;

  const handleToggleStoreStatus = async (store) => {
    const storeId = getStoreId(store);
    if (!storeId) return;
    setStoreStatusError("");
    const status = getStoreStatus(store);
    const nextStatus = status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await updateStoreStatus({
        businessId,
        storeId,
        payload: { status: nextStatus },
      }).unwrap();
    } catch (err) {
      setStoreStatusError(
        err?.data?.detail || "Failed to update store status.",
      );
    }
  };

  const storeColumns = [
    { key: "storeName", label: "Store Name" },
    { key: "city", label: "City" },
    { key: "currencyCode", label: "Currency" },
    { key: "status", label: "Status" },
    { key: "createdAt", label: "Created" },
    { key: "actions", label: "Actions" },
  ];

  const storeRows = stores.map((store) => {
    const storeId = getStoreId(store);
    const status = getStoreStatus(store);
    const isActive = status === "ACTIVE";
    return {
      id: storeId,
      storeName: getStoreName(store),
      city: getStoreCity(store),
      currencyCode: getStoreCurrencyCode(store, business) || "-",
      status: (
        <div className="flex items-center gap-2">
          <ActionCheckbox
            checked={isActive}
            label={isActive ? "Active" : "Inactive"}
            disabled={updateStoreStatusState.isLoading}
            onChange={() => handleToggleStoreStatus(store)}
          />
          <span className="text-sm text-slate-700">
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
      ),
      createdAt: formatDateTime(getStoreCreatedAt(store)),
      actions: (
        <div className="flex flex-wrap items-center gap-2">
          <ActionButton
            label="View"
            icon="view"
            onClick={() => navigate(`/businesses/${businessId}/stores/${storeId}`)}
          />
          <ActionButton
            label="Edit"
            icon="edit"
            onClick={() => navigate(`/businesses/${businessId}/stores/${storeId}/edit`)}
          />
        </div>
      ),
    };
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Business Details" subtitle="Loading..." />
        <Card>
          <p className="text-sm text-slate-500">Loading business details...</p>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Business Details"
          subtitle="Error loading business"
        />
        <Card>
          <Alert tone="error">
            {error?.data?.detail || "Failed to load business details."}
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Business Information"
        actions={
          <div className="flex gap-3">
            <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
            {!isEditing && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
          </div>
        }
      />

      <Card className="p-0">
        <div className="py-4">
          <div className="mx-6 rounded-xl border border-slate-100 bg-slate-50/60 p-5">
            <div className="grid gap-5 lg:grid-cols-[auto,1fr] lg:items-start">
            <div className="w-full lg:w-40">
              <div>
                {logoPreview || business?.logoUrl ? (
                  <img
                    src={logoPreview || business?.logoUrl}
                    alt="Business logo"
                    className="h-24 w-24 rounded-lg border border-slate-200 object-cover"
                  />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50">
                    <svg
                      className="h-9 w-9 text-slate-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                )}
              </div>
              {isEditing && (
                <>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={handleLogoChange}
                        className="hidden"
                      />
                      <span className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900">
                        Choose File
                      </span>
                    </label>
                    {logoFile && (
                      <Button
                        size="sm"
                        onClick={handleUploadLogo}
                        disabled={uploadState.isLoading}
                      >
                        {uploadState.isLoading ? "Uploading..." : "Upload"}
                      </Button>
                    )}
                    {business?.logoUrl && !logoFile && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setShowDeleteModal(true)}
                        disabled={deleteLogoState.isLoading}
                      >
                        Remove Logo
                      </Button>
                    )}
                  </div>
                  {uploadState.isError && (
                    <Alert tone="error" className="mt-3">
                      {uploadState.error?.data?.detail || "Failed to upload logo."}
                    </Alert>
                  )}
                </>
              )}
            </div>

              {!isEditing && (
                <dl className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Status
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-slate-900">
                    {business?.status || "-"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Business Name
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-slate-900">
                    {business?.businessName}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Legal Name
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-slate-900">
                    {business?.legalName || "-"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Contact Email
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-slate-900">
                    {business?.contactEmail}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Contact Phone
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-slate-900">
                    {business?.contactPhone || "-"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Country
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-slate-900">
                    {business?.countryCode || "-"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Business Currency
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-slate-900">
                    {getBusinessCurrencyCode(business) || "-"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Time Zone
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-slate-900">
                    {business?.timeZone || "-"}
                  </dd>
                </div>
                </dl>
              )}

              {isEditing && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      label="Business Name"
                      name="businessName"
                      value={form.businessName}
                      onChange={handleChange}
                      required
                    />
                    <Input
                      label="Legal Name"
                      name="legalName"
                      value={form.legalName}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      label="Contact Email"
                      name="contactEmail"
                      type="email"
                      value={form.contactEmail}
                      onChange={handleChange}
                      required
                    />
                    <Input
                      label="Contact Phone"
                      name="contactPhone"
                      value={form.contactPhone}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <Input
                      label="Country Code"
                      name="countryCode"
                      value={form.countryCode}
                      onChange={handleChange}
                      placeholder="US"
                    />
                    <Input
                      label="Currency Code"
                      name="currencyCode"
                      value={form.currencyCode}
                      onChange={handleChange}
                      placeholder="USD"
                    />
                    <Input
                      label="Time Zone"
                      name="timeZone"
                      value={form.timeZone}
                      onChange={handleChange}
                      placeholder="America/New_York"
                    />
                  </div>

                  {updateState.isError && (
                    <Alert tone="error">
                      {updateState.error?.data?.detail ||
                        "Failed to update business."}
                    </Alert>
                  )}

                  <div className="flex gap-3">
                    <Button type="submit" disabled={updateState.isLoading}>
                      {updateState.isLoading ? "Saving..." : "Save Changes"}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setIsEditing(false);
                        if (business) {
                          setForm({
                            businessName: business.businessName || "",
                            legalName: business.legalName || "",
                            logoUrl: business.logoUrl || "",
                            status: business.status || "ACTIVE",
                            countryCode: business.countryCode || "",
                            timeZone: business.timeZone || "",
                            currencyCode: getBusinessCurrencyCode(business),
                            contactEmail: business.contactEmail || "",
                            contactPhone: business.contactPhone || "",
                          });
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 pb-4 pt-3 text-base font-semibold text-slate-900">
          Stores
        </div>
        <div className="px-6 py-4">
          {isStoresLoading && (
            <p className="text-sm text-slate-500">Loading stores...</p>
          )}
          {isStoresError && (
            <Alert tone="error">
              {storesError?.data?.detail || "Failed to load stores."}
            </Alert>
          )}
          {storeStatusError && (
            <Alert tone="error" className="mb-3">
              {storeStatusError}
            </Alert>
          )}
          {!isStoresLoading && !isStoresError && (
            <Table
              columns={storeColumns}
              rows={storeRows}
              emptyText="No stores yet. Create your first store."
            />
          )}
        </div>
      </Card>

      {/* Delete Logo Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Remove Logo"
        message="Are you sure you want to remove the business logo?"
        onConfirm={handleDeleteLogo}
        confirmText="Remove"
        isLoading={deleteLogoState.isLoading}
      />
    </div>
  );
};

export default BusinessDetail;
